import ApiService from '../../../services/ApiService';
import Logger from '../../../services/Logger';

/**
 * Service for jigsaw puzzle play functionality
 * Base path: /home/jigsaw-play
 */
const JigsawPlayService = {
    /**
     * GET /home/jigsaw-play - List play sessions (paged)
     * @param {Object} options - Query parameters
     * @param {number} [options.page=0] - 0-based page index
     * @param {number} [options.pageSize=20] - Page size
     * @param {string} [options.sort='updatedAt:desc'] - Sort order
     * @param {string} [options.jigsawId] - Filter to one jigsaw row UUID
     * @param {string} [options.keyword] - Substring match on linked cloud user
     * @param {boolean} [options.isCompleted=false] - Filter completed vs in-progress
     * @param {boolean} [options.onlyStarted] - Only sessions with saved layout JSON
     * @returns {Promise<Object>} Spring Page response with content array
     */
    list: async ({
        page = 0,
        pageSize = 20,
        sort = 'updatedAt:desc',
        jigsawId,
        keyword,
        isCompleted = false,
        onlyStarted
    } = {}) => {
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
                sort: String(sort),
                isCompleted: String(isCompleted)
            });

            if (jigsawId) params.set('jigsawId', jigsawId);
            if (keyword) params.set('keyword', keyword);
            if (onlyStarted !== undefined) params.set('onlyStarted', String(onlyStarted));

            const response = await ApiService.getData(`/home/jigsaw-play?${params.toString()}`);
            
            if (response && response.code === 'FAILURE') {
                return {
                    success: false,
                    error: response.message || 'Failed to fetch jigsaw play sessions'
                };
            }

            return {
                success: true,
                data: {
                    content: response?.content || [],
                    totalElements: response?.totalElements || 0,
                    totalPages: response?.totalPages || 0,
                    number: response?.number || 0,
                    size: response?.size || pageSize,
                    hasNext: !response?.last
                }
            };
        } catch (error) {
            Logger.error('Error fetching jigsaw play sessions:', error);
            return {
                success: false,
                error: error.message || 'Failed to fetch jigsaw play sessions'
            };
        }
    },

    /**
     * POST /home/jigsaw-play/start - Start or resume session
     * @param {Object} data - Request body
     * @param {string} data.jigsawId - Target jigsaw row UUID
     * @param {*} [data.scatteredPieces] - Required on first start only; omit on resume
     * @param {*} [data.boardPositions] - Required on first start only; omit on resume
     * @returns {Promise<Object>} JigsawPlayRo response
     */
    start: async ({ jigsawId, scatteredPieces, boardPositions }) => {
        try {
            if (!jigsawId) {
                return {
                    success: false,
                    error: 'jigsawId is required'
                };
            }

            const requestBody = { jigsawId };
            if (scatteredPieces !== undefined) requestBody.scatteredPieces = scatteredPieces;
            if (boardPositions !== undefined) requestBody.boardPositions = boardPositions;

            const response = await ApiService.postData('/home/jigsaw-play/start', requestBody);

            if (response && response.code === 'FAILURE') {
                return {
                    success: false,
                    error: response.message || 'Failed to start jigsaw play session'
                };
            }

            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error('Error starting jigsaw play session:', error);
            return {
                success: false,
                error: error.message || 'Failed to start jigsaw play session'
            };
        }
    },

    /**
     * POST /home/jigsaw-play/restart - Restart session
     * @param {Object} data - Request body (same as start)
     * @param {string} data.jigsawId - Target jigsaw row UUID
     * @param {*} data.scatteredPieces - Tray/off-board coordinates (JSON)
     * @param {*} data.boardPositions - On-board layout (JSON)
     * @returns {Promise<Object>} JigsawPlayRo response
     */
    restart: async ({ jigsawId, scatteredPieces, boardPositions }) => {
        try {
            if (!jigsawId) {
                return {
                    success: false,
                    error: 'jigsawId is required'
                };
            }

            if (scatteredPieces == null || boardPositions == null) {
                return {
                    success: false,
                    error: 'scatteredPieces and boardPositions are required'
                };
            }

            const requestBody = {
                jigsawId,
                scatteredPieces,
                boardPositions
            };

            const response = await ApiService.postData('/home/jigsaw-play/restart', requestBody);

            if (response && response.code === 'FAILURE') {
                return {
                    success: false,
                    error: response.message || 'Failed to restart jigsaw play session'
                };
            }

            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error('Error restarting jigsaw play session:', error);
            return {
                success: false,
                error: error.message || 'Failed to restart jigsaw play session'
            };
        }
    },

    /**
     * PUT /home/jigsaw-play/progress - Save progress
     * @param {Object} data - Request body
     * @param {string} data.jigsawId - Target jigsaw row UUID
     * @param {*} [data.scatteredPieces] - Updated tray/off-board coordinates
     * @param {*} [data.boardPositions] - Updated on-board layout
     * @param {*} [data.currentState] - Optional extra blob
     * @param {boolean} [data.isCompleted] - Mark session as completed
     * @returns {Promise<Object>} Updated JigsawPlayRo response
     */
    progress: async ({ jigsawId, scatteredPieces, boardPositions, currentState, isCompleted }) => {
        try {
            if (!jigsawId) {
                return {
                    success: false,
                    error: 'jigsawId is required'
                };
            }

            const requestBody = { jigsawId };
            
            // Only include fields that are explicitly provided
            if (scatteredPieces !== undefined) requestBody.scatteredPieces = scatteredPieces;
            if (boardPositions !== undefined) requestBody.boardPositions = boardPositions;
            if (currentState !== undefined) requestBody.currentState = currentState;
            if (isCompleted !== undefined) requestBody.isCompleted = isCompleted;

            const response = await ApiService.putData('/home/jigsaw-play/progress', requestBody);

            if (response && response.code === 'FAILURE') {
                return {
                    success: false,
                    error: response.message || 'Failed to save jigsaw play progress'
                };
            }

            // Handle 400 errors with error field
            if (response && response.error) {
                return {
                    success: false,
                    error: response.error
                };
            }

            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error('Error saving jigsaw play progress:', error);
            return {
                success: false,
                error: error.message || 'Failed to save jigsaw play progress'
            };
        }
    },

    /**
     * PUT /home/jigsaw-play/force-place-pieces/{count} - Snap off-board pieces onto the board (local profile only)
     * @param {number} count - Positive integer; max pieces to place
     * @param {Object} data - Request body
     * @param {string} data.jigsawId - Target jigsaw row UUID
     * @returns {Promise<Object>} Updated JigsawPlayRo response
     */
    forcePlacePieces: async (count, { jigsawId }) => {
        try {
            if (!jigsawId) {
                return {
                    success: false,
                    error: 'jigsawId is required'
                };
            }

            const n = Number(count);
            if (!Number.isInteger(n) || n <= 0) {
                return {
                    success: false,
                    error: 'count must be a positive integer'
                };
            }

            const response = await ApiService.putData(
                `/home/jigsaw-play/force-place-pieces/${n}`,
                { jigsawId }
            );

            if (response && response.code === 'FAILURE') {
                return {
                    success: false,
                    error: response.message || 'Failed to force-place jigsaw pieces'
                };
            }

            if (response && response.error) {
                return {
                    success: false,
                    error: response.error
                };
            }

            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error('Error force-placing jigsaw pieces:', error);
            return {
                success: false,
                error: error.message || 'Failed to force-place jigsaw pieces'
            };
        }
    }
};

export default JigsawPlayService;